/**
 * BiomeTextureModule — the reusable stylesheet layer that maps tile types
 * (FLOOR, GRASS, WALL, LAVA, …) to biome-specific texture-sheet keys + tints
 * based on the active biome, so a level swapping biomes can re-skin its ground,
 * collision, and background tiles in one resolved lookup instead of per-sprite
 * branching.
 *
 * DESIGN — family bases + per-biome overrides (the "inherit from base
 * configurations" pattern, applied to tile stylesheets):
 *   • 8 family base profiles (cold / hot / toxic / temperate / dark / aquatic /
 *     bright / ethereal) carry the shared tile palette + collision/ground/
 *     background category defaults for a biome character.
 *   • Each atmosphere-preset biome id is mapped to a family, then layered with a thin
 *     per-biome override (its horizon `background` tint + any distinguishing
 *     tile). The resolver merges: biome-override tile → family tile → family
 *     category default → global default.
 *
 * `textureKey` is a stable, namespaced sheet identifier (`tile_<tile>_<family>`,
 * `tile_<tile>_<biome>` for overrides, `bg_<biome>` for backgrounds) the renderer
 * maps to an actual Pixi texture/sheet when one is registered; until then the
 * `tint` alone re-skins the existing flat-colour tiles. Pure data + selectors —
 * no renderer wiring here (additive foundation; see `resolveTileStyle`).
 */
import { TileType, SurfaceType, getTileColor } from '../../../shared/TileEditorTypes';
import { cats, tile, FAMILY_PROFILES, WICKHAVEN_TILE_PROFILE } from './biomeTileProfiles';

/** A resolved tile appearance for the active biome. */
export interface BiomeTileStyle {
  /** Texture-sheet key the renderer binds (e.g. `tile_floor_hot`, `bg_arctic`). */
  textureKey: string;
  /** 0xRRGGBB multiply tint applied to the sprite. */
  tint: number;
}

/** Coarse tile component category the renderer swaps per biome. */
export type TileCategory = 'ground' | 'collision' | 'background';

/** A family base or per-biome texture profile. */
export interface BiomeTextureProfile {
  /** Per-tile-type style overrides (the distinguishing tiles for this character). */
  tiles?: Partial<Record<TileType, BiomeTileStyle>>;
  /** Category defaults (ground / collision / background). */
  categories: Record<TileCategory, BiomeTileStyle>;
}

/** The 8 biome characters a base profile is authored for. */
export type BiomeTileFamily =
  | 'cold' | 'hot' | 'toxic' | 'temperate' | 'dark' | 'aquatic' | 'bright' | 'ethereal';

// ─── biome → family ─────────────────────────────────────────────────────────
const BIOME_FAMILY: Record<string, BiomeTileFamily> = {
  cozy_forest: 'temperate',
  subtle: 'temperate',
  dungeon: 'dark',
  hush_nook: 'dark',
  cave: 'dark',
  volcanic: 'hot',
  arctic: 'cold',
  underwater: 'aquatic',
  desert: 'hot',
  corrupted: 'toxic',
  swamp: 'toxic',
  fungal: 'toxic',
  jungle: 'temperate',
  meadow: 'bright',
  celestial: 'ethereal',
  autumn: 'hot',
  ashlands: 'dark',
  tempest: 'cold',
  coast: 'aquatic',
  radiant: 'bright',
  moor: 'cold',
  savanna: 'hot',
  skylands: 'bright',
  bamboo: 'temperate',
  abyss: 'dark',
  twilight: 'temperate',
  frostspire: 'cold',
  crimson: 'hot',
  petrified: 'dark',
  sakura: 'temperate',
  nexus: 'ethereal',
  arena: 'hot',
  // Cross-biome leviathan showcases.
  sunken_drift: 'aquatic',
  sky_whale: 'ethereal',
  void_serpent: 'dark',
  fossil_graveyard: 'dark',
  // Hollow Coil cozy→spooky gradient.
  pig_beach: 'bright',
  tidewend: 'temperate',
  tidewend_port: 'temperate',
  wickhaven: 'temperate',
  wickhaven_port: 'temperate',
  tidewend_outskirts: 'temperate',
  drowned_road: 'cold',
  lanternwood: 'cold',
  brackenmoor: 'cold',
  coil_undercroft: 'dark',
  // On-Ramp Restructure — map 1 of 3 (The Waking Vault).
  tomb_waking: 'dark',
  // On-Ramp Restructure S3 — moonlit village refuge.
  village_arrival: 'temperate',
  // On-Ramp Restructure S4 — moonlit starter combat field.
  first_field: 'temperate',
  // Hollow Coil side branches.
  moonfall_hollow: 'dark',
  lanternroot_hollow: 'dark',
  moonfen_pasture: 'cold',
  // Connected woodland journey, including the existing Dark Forest scene alias.
  forest_road: 'temperate',
  deep_woods: 'temperate',
  abandoned_camp: 'temperate',
  old_grove: 'temperate',
};

/** Explicit forest surfaces override the temperate family's brighter generic
 * turf; category defaults and actual FLOOR/WALL tiles must agree. */
function forestProfile(scope: string, p: Readonly<{
  ground: number; collision: number; background: number; grass: number; wood: number; stone: number;
}>): BiomeTextureProfile {
  return {
    tiles: {
      [TileType.FLOOR]: tile(TileType.FLOOR, scope, p.ground),
      [TileType.WALL]: tile(TileType.WALL, scope, p.collision),
      [TileType.GRASS]: tile(TileType.GRASS, scope, p.grass),
      [TileType.WOOD]: tile(TileType.WOOD, scope, p.wood),
      [TileType.STONE]: tile(TileType.STONE, scope, p.stone),
    },
    categories: cats(scope, p.ground, p.collision, p.background),
  };
}

// ─── per-biome overrides (horizon `background` tint + distinguishing tiles) ──
// The background tint is each biome's sky-gradient horizon (the `to` colour),
// so the backdrop re-skins with the biome even before tile sheets are wired.
const BIOME_OVERRIDES: Record<string, BiomeTextureProfile> = {
  wickhaven: WICKHAVEN_TILE_PROFILE,
  // Ground/wood/stone match the authored journey palette; backgrounds match
  // forestJourney's sky horizons. Existing boundary-wall colors are retained.
  forest_road: forestProfile('forest_road', { ground: 0x3c4135, collision: 0x2f2f2f,
    background: 0x59755c, grass: 0x6b7859, wood: 0x504032, stone: 0x738174 }),
  deep_woods: forestProfile('deep_woods', { ground: 0x293e32, collision: 0x10231c,
    background: 0x3d5843, grass: 0x62794f, wood: 0x4a3c30, stone: 0x74806b }),
  abandoned_camp: forestProfile('abandoned_camp', { ground: 0x3c4133, collision: 0x10231c,
    background: 0x687356, grass: 0x807751, wood: 0x685640, stone: 0x7c8474 }),
  old_grove: forestProfile('old_grove', { ground: 0x293e32, collision: 0x10231c,
    background: 0x435b46, grass: 0x6b825d, wood: 0x44503b, stone: 0x86937d }),
  cozy_forest: { categories: cats('cozy_forest', 0x5a4a3a, 0x3a4a2a, 0x3a5230) },
  subtle: { categories: cats('subtle', 0x5a4a3a, 0x3a4a2a, 0x1a1a2e) },
  dungeon: { categories: cats('dungeon', 0x2a2018, 0x100b08, 0x33241a) },
  cave: { categories: cats('cave', 0x163446, 0x0e1a26, 0x163446) },
  volcanic: { categories: cats('volcanic', 0x3a2418, 0x140a06, 0x6e2410) },
  arctic: { categories: cats('arctic', 0x6b7e8c, 0x1e2c3a, 0x44637e) },
  underwater: { categories: cats('underwater', 0x0e3e46, 0x08272e, 0x05161c) },
  desert: {
    tiles: { [TileType.FLOOR]: tile(TileType.FLOOR, 'desert', 0xd9a85c), [TileType.GRASS]: tile(TileType.GRASS, 'desert', 0xc4b86a) },
    categories: cats('desert', 0xd9a85c, 0x7a5128, 0xe0b574),
  },
  corrupted: { categories: cats('corrupted', 0x1d2a1c, 0x0c0416, 0x2e0f3e) },
  swamp: { categories: cats('swamp', 0x1d3a2c, 0x0a140e, 0x1d3a2c) },
  fungal: {
    tiles: { [TileType.GRASS]: tile(TileType.GRASS, 'fungal', 0xd633ff) },
    categories: cats('fungal', 0x2a0a30, 0x12061a, 0x2a0a30),
  },
  jungle: { categories: cats('jungle', 0x0c2415, 0x081a0e, 0x356b34) },
  meadow: { categories: cats('meadow', 0x5a4a3a, 0x3a4a2a, 0xb9e08c) },
  celestial: { categories: cats('celestial', 0x2a1a4a, 0x241848, 0x2a1a52) },
  autumn: { categories: cats('autumn', 0x5a3a1e, 0x35200e, 0xe6a85a) },
  ashlands: { categories: cats('ashlands', 0x42403c, 0x110f0d, 0x4a443e) },
  tempest: { categories: cats('tempest', 0x2a3744, 0x10171e, 0x33414f) },
  coast: { categories: cats('coast', 0xd6b274, 0x4a7d5e, 0xf2e3b4) },
  radiant: { categories: cats('radiant', 0x8f6c30, 0x5a4a22, 0xffe2a0) },
  moor: { categories: cats('moor', 0x6f8273, 0x3f4d46, 0xb2bbb4) },
  savanna: { categories: cats('savanna', 0x7a5532, 0x35200f, 0xf5c66a) },
  skylands: { categories: cats('skylands', 0xaccfe8, 0x2c4a63, 0xb8e0ff) },
  bamboo: { categories: cats('bamboo', 0x537f38, 0x24341a, 0xe4ecae) },
  abyss: { categories: cats('abyss', 0x081821, 0x01060a, 0x06141f) },
  twilight: { categories: cats('twilight', 0x4a3c72, 0x211b3c, 0xe5835e) },
  frostspire: { categories: cats('frostspire', 0x9fbcd8, 0x546f93, 0xbfd8ec) },
  crimson: { categories: cats('crimson', 0x2a060e, 0x100207, 0x5a0a16) },
  petrified: { categories: cats('petrified', 0x6f665a, 0x3c352e, 0x9c9082) },
  sakura: {
    tiles: { [TileType.GRASS]: tile(TileType.GRASS, 'sakura', 0xffd0e0) },
    categories: cats('sakura', 0x7d5566, 0x3a2230, 0xf6cdda),
  },
  nexus: { categories: cats('nexus', 0x2a1a4a, 0x241a42, 0x241848) },
  arena: { categories: cats('arena', 0x2a1730, 0x0a0410, 0x4a2342) },
  // Cross-biome leviathan showcases — tile tints match each biome's atmosphere palette.
  sunken_drift: {
    // Wet-stone vault: dark teal-tinted stone + cold caustic water.
    tiles: {
      [TileType.STONE]: tile(TileType.STONE, 'sunken_drift', 0x0c2a26),
      [TileType.FLOOR]: tile(TileType.FLOOR, 'sunken_drift', 0x0a1f1c),
      [TileType.WATER]: tile(TileType.WATER, 'sunken_drift', 0x1fb6a6),
    },
    categories: cats('sunken_drift', 0x0a1f1c, 0x05100e, 0x0c2a26),
  },
  sky_whale: {
    // Bright dawn sky: warm sandy floors + pale cloud-white stone.
    tiles: {
      [TileType.STONE]: tile(TileType.STONE, 'sky_whale', 0xe8d0c0),
      [TileType.FLOOR]: tile(TileType.FLOOR, 'sky_whale', 0xf0d4b8),
    },
    categories: cats('sky_whale', 0xf0d4b8, 0xc8b0a0, 0xf5e3b0),
  },
  void_serpent: {
    // Near-black abyss: dark navy stone + faint cyan water.
    tiles: {
      [TileType.STONE]: tile(TileType.STONE, 'void_serpent', 0x0c0c20),
      [TileType.FLOOR]: tile(TileType.FLOOR, 'void_serpent', 0x0a0a1a),
    },
    categories: cats('void_serpent', 0x0a0a1a, 0x050510, 0x121228),
  },
  fossil_graveyard: {
    // Dry stone vault: dark amber-brown sediment + bone-dust floors.
    tiles: {
      [TileType.STONE]: tile(TileType.STONE, 'fossil_graveyard', 0x2a2418),
      [TileType.FLOOR]: tile(TileType.FLOOR, 'fossil_graveyard', 0x1a1812),
    },
    categories: cats('fossil_graveyard', 0x1a1812, 0x080604, 0x0e0c08),
  },
  // Hollow Coil cozy→spooky gradient — tile tints shift with the atmosphere.
  pig_beach: {
    // Sunny shore: warm sandy floors + palm-green grass + bright water.
    tiles: {
      [TileType.FLOOR]: tile(TileType.FLOOR, 'pig_beach', 0xd6b274),
      [TileType.GRASS]: tile(TileType.GRASS, 'pig_beach', 0x6f9e86),
      [TileType.WATER]: tile(TileType.WATER, 'pig_beach', 0x4a9acf),
    },
    categories: cats('pig_beach', 0xd6b274, 0x4a7d5e, 0xf5e3b0),
  },
  tidewend: {
    // Warm dusk village: amber-tinted wood + warm cobblestone.
    tiles: {
      [TileType.WOOD]: tile(TileType.WOOD, 'tidewend', 0x6a4a32),
      [TileType.STONE]: tile(TileType.STONE, 'tidewend', 0x4a3020),
    },
    categories: cats('tidewend', 0x5a3a1e, 0x2a1a10, 0x6b4a6e),
  },
  tidewend_outskirts: {
    categories: cats('tidewend_outskirts', 0x354238, 0x202a25, 0x34354c),
  },
  drowned_road: {
    tiles: {
      [TileType.STONE]: tile(TileType.STONE, 'drowned_road', 0x4b5559),
      [TileType.WATER]: tile(TileType.WATER, 'drowned_road', 0x284f66),
    },
    categories: cats('drowned_road', 0x374247, 0x1c282d, 0x23344f),
  },
  hush_nook: {
    // Mallow's cellar: teal flagstone floor, dark timber, warm lamp-lit wall.
    tiles: {
      [TileType.STONE]: tile(TileType.STONE, 'hush_nook', 0x3b4a45),
      [TileType.WOOD]: tile(TileType.WOOD, 'hush_nook', 0x4a3a2a),
    },
    categories: cats('hush_nook', 0x2f3d39, 0x18221f, 0x5a4a34),
  },
  lanternwood: {
    tiles: {
      [TileType.GRASS]: tile(TileType.GRASS, 'lanternwood', 0x4f6355),
      [TileType.WOOD]: tile(TileType.WOOD, 'lanternwood', 0x3f3528),
    },
    categories: cats('lanternwood', 0x46594c, 0x202b27, 0x596b62),
  },
  brackenmoor: {
    // Moonlit grey-green moor: readable surfaces without a daylight wash.
    tiles: {
      [TileType.FLOOR]: tile(TileType.FLOOR, 'brackenmoor', 0x34433b),
      [TileType.GRASS]: tile(TileType.GRASS, 'brackenmoor', 0x405347),
      [TileType.STONE]: tile(TileType.STONE, 'brackenmoor', 0x3c4a48),
    },
    categories: cats('brackenmoor', 0x405347, 0x263330, 0x667a77),
  },
  coil_undercroft: {
    // Sickly-green undercroft: dark green-tinted stone + cold corruption.
    tiles: {
      [TileType.STONE]: tile(TileType.STONE, 'coil_undercroft', 0x12231a),
      [TileType.FLOOR]: tile(TileType.FLOOR, 'coil_undercroft', 0x06090a),
    },
    categories: cats('coil_undercroft', 0x06090a, 0x06090a, 0x12231a),
  },
  // On-Ramp Restructure map 1 — candle-lit wet sepulcher (tombWaking preset):
  // cold grey wet stone with a faint warm cast, near-black floors, flooded-alcove
  // teal water; horizon matches the preset sky `to` (0x1a1a20).
  tomb_waking: {
    tiles: {
      [TileType.STONE]: tile(TileType.STONE, 'tomb_waking', 0x3c3a38),
      [TileType.FLOOR]: tile(TileType.FLOOR, 'tomb_waking', 0x232226),
      [TileType.WATER]: tile(TileType.WATER, 'tomb_waking', 0x224444),
    },
    categories: cats('tomb_waking', 0x232226, 0x141416, 0x1a1a20),
  },
  // Moonlit village: cool ground/collision under localized amber hearth light.
  village_arrival: {
    tiles: {
      [TileType.WOOD]: tile(TileType.WOOD, 'village_arrival', 0x5a4232),
      [TileType.STONE]: tile(TileType.STONE, 'village_arrival', 0x3a3b48),
    },
    categories: cats('village_arrival', 0x4b3a35, 0x232532, 0x30233a),
  },
  // Moonlit field: readable desaturated turf against a deep blue horizon.
  first_field: {
    categories: cats('first_field', 0x354238, 0x1d2b25, 0x26324d),
  },
};

// Ports deliberately share their city's tile identity, just as their atmosphere aliases do.
BIOME_OVERRIDES.tidewend_port = BIOME_OVERRIDES.tidewend;
BIOME_OVERRIDES.wickhaven_port = BIOME_OVERRIDES.wickhaven;

/** The global fallback profile (flat palette colours) for unknown biomes. */
const DEFAULT_PROFILE: BiomeTextureProfile = {
  tiles: {
    [TileType.FLOOR]: { textureKey: 'tile_floor_default', tint: parseInt(getTileColor(TileType.FLOOR).slice(1), 16) },
    [TileType.GRASS]: { textureKey: 'tile_grass_default', tint: parseInt(getTileColor(TileType.GRASS).slice(1), 16) },
    [TileType.WALL]: { textureKey: 'tile_wall_default', tint: parseInt(getTileColor(TileType.WALL).slice(1), 16) },
    [TileType.LAVA]: { textureKey: 'tile_lava_default', tint: parseInt(getTileColor(TileType.LAVA).slice(1), 16) },
    [TileType.WATER]: { textureKey: 'tile_water_default', tint: parseInt(getTileColor(TileType.WATER).slice(1), 16) },
    [TileType.ICE]: { textureKey: 'tile_ice_default', tint: parseInt(getTileColor(TileType.ICE).slice(1), 16) },
    [TileType.STONE]: { textureKey: 'tile_stone_default', tint: parseInt(getTileColor(TileType.STONE).slice(1), 16) },
    [TileType.WOOD]: { textureKey: 'tile_wood_default', tint: parseInt(getTileColor(TileType.WOOD).slice(1), 16) },
  },
  categories: cats('default', 0x8b7355, 0x4a4a4a, 0x1a1a2e),
};

/** The tile category a TileType renders as (mirrors `getTileLayer`'s grouping). */
export function tileCategoryOf(tileType: TileType): TileCategory {
  switch (tileType) {
  case TileType.WALL:
  case TileType.DOOR:
  case TileType.WINDOW:
  case TileType.HALF_WALL:
    return 'collision';
  default:
    return 'ground';
  }
}

/** The 8-family partition, exposed for family-keyed consumers (the
 *  world-combat reactivity response table keys on family, not per-biome —
 *  spec §2.6). null = biome has no authored family (unknown/custom id). */
export function biomeFamilyOf(biome: string | null | undefined): BiomeTileFamily | null {
  if (!biome) return null;
  return BIOME_FAMILY[biome] ?? null;
}

/** The merged texture profile for a biome (family base + per-biome override).
 *  Falls back to the global default profile for unknown biome ids. */
export function biomeTextureProfile(biome: string | null | undefined): BiomeTextureProfile {
  if (!biome) return DEFAULT_PROFILE;
  const family = BIOME_FAMILY[biome];
  const override = BIOME_OVERRIDES[biome];
  if (!family && !override) return DEFAULT_PROFILE;
  const base = family ? FAMILY_PROFILES[family] : DEFAULT_PROFILE;
  if (!override) return base;
  return {
    tiles: { ...base.tiles, ...override.tiles },
    categories: { ...base.categories, ...override.categories },
  };
}

/** Does this biome id have an authored tile profile (a family base and/or a
 *  per-biome override)? Renderers gate on this to decide whether to re-skin
 *  tiles with `resolveTileStyle` or keep the platform's authored flat colour —
 *  so a map with no biome profile degrades gracefully instead of falling to
 *  the global default palette (which would silently recolour authored art). */
export function biomeHasTileProfile(biome: string | null | undefined): boolean {
  if (!biome) return false;
  return Boolean(BIOME_FAMILY[biome] || BIOME_OVERRIDES[biome]);
}

/** Map a platform's secondary `SurfaceType` overlay to the TileType whose
 *  biome tint re-skins it (GRASS surface → GRASS tile tint, sandy surface →
 *  FLOOR, snow → ICE, …). Pairs with `resolveTileStyle` so the surface band
 *  tracks the biome alongside the platform body. */
export function surfaceTypeToTileType(surface: SurfaceType): TileType {
  switch (surface) {
  case SurfaceType.GRASS:
  case SurfaceType.FLOWERS:
  case SurfaceType.VINES:
    return TileType.GRASS;
  case SurfaceType.SAND:
    return TileType.FLOOR;
  case SurfaceType.SNOW:
    return TileType.ICE;
  case SurfaceType.MOSS:
    return TileType.STONE;
  case SurfaceType.WATER_SHALLOW:
    return TileType.WATER;
  case SurfaceType.LAVA_CRACKS:
    return TileType.LAVA;
  default:
    return TileType.GRASS;
  }
}

/** Resolve a single tile's style for the active biome.
 *  Merge order: biome-override tile → family tile → family category default →
 *  global default tile → global category default. */
export function resolveTileStyle(biome: string | null | undefined, tileType: TileType): BiomeTileStyle {
  const profile = biomeTextureProfile(biome);
  const fromTiles = profile.tiles?.[tileType];
  if (fromTiles) return fromTiles;
  const cat = tileCategoryOf(tileType);
  const fromCat = profile.categories[cat];
  if (fromCat) return fromCat;
  const defTiles = DEFAULT_PROFILE.tiles?.[tileType];
  if (defTiles) return defTiles;
  return DEFAULT_PROFILE.categories[tileCategoryOf(tileType)];
}

/** Resolve a category default style (ground / collision / background) for a biome
 *  — the "component selector" a renderer calls to swap a whole tile layer at once. */
export function tileCategoryStyle(biome: string | null | undefined, category: TileCategory): BiomeTileStyle {
  return biomeTextureProfile(biome).categories[category] ?? DEFAULT_PROFILE.categories[category];
}

/** Resolve every tile type's style for a biome in one pass (e.g. to bulk-update
 *  a tile sprite pool on a biome switch). */
export function resolveAllTileStyles(biome: string | null | undefined): Record<TileType, BiomeTileStyle> {
  const out = {} as Record<TileType, BiomeTileStyle>;
  for (const t of Object.values(TileType).filter((v): v is TileType => typeof v === 'number')) {
    out[t] = resolveTileStyle(biome, t);
  }
  return out;
}

/** The set of biome ids with an authored texture profile (every atmosphere-preset biome). */
/** Every biome with either a family base or a per-biome override. */
export const BIOME_TEXTURE_BIOMES: readonly string[] = [...new Set([
  ...Object.keys(BIOME_FAMILY),
  ...Object.keys(BIOME_OVERRIDES),
])];
